import { EmbeddedAddToCartCallback } from '@app/components/EmbeddedAddToCartButton/EmbeddedAddToCartButton';
import { CartCheckoutClient } from '@app/data/clients/cartCheckoutClient';
import { useUpdateCartItemsCount } from '@redbubble/next/cart-items-count';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useCallback } from 'react';

const cartCheckoutClient = new CartCheckoutClient({
  logging: {
    context: 'hooks/useAddItemToCart',
    metricName: 'frontend.useAddItemToCart.errors.count',
  },
});

/**
 * Add an item to the users cart. Goes through the monolith /cart/items endpoint via the fep api.
 * Monolith automatically manages anonymous user persistence if they weren't persisted prior.
 * If a user was persisted and already authed then item simply gets added to their cart.
 * See: redbubble/app/controllers/cart/items_controller.rb ::create
 *
 * This hook is explicitly different from cartCheckoutClient/CartAndDeliveryDatesDataContext/hooks/useAddCartLineItem.ts
 * as the SRP and ArtistShop do not require complex dispatching of events to update cart contexts. All they need is add
 * to cart and return a response to show success. This simply updates the UI counter and we're done.
 */
export const useAddItemToCart = (
  onSuccessCallback?: (itemId: string, index: number) => void
) => {
  const updateCartItemsCount = useUpdateCartItemsCount();
  const [{ userId }, setUserInfo] = useUserInfo<UserInfo>();

  return useCallback<EmbeddedAddToCartCallback>(
    async (itemId, index) => {
      const response = await cartCheckoutClient
        .addCartLineItem({
          inventory_item_id: itemId,
          quantity: 1,
        })
        .catch(() => {
          // Error response has historically been unhandled by the SRP and simply returns null (quick ATC stays in
          // loading state). Improvement in the future could see an 'onFailCallback', or errors handled at the call sites.
          return null;
        });

      if (!response || 'errors' in response) return null;

      const { cart } = response;
      // If the cart addition completed, but our user was anonymous, the cart endpoint persisted the user for us.
      if (!userId)
        setUserInfo((prevInfo) => ({
          ...prevInfo,
          userId: cart.user.id,
        }));

      updateCartItemsCount(cart.total_items_count);
      if (onSuccessCallback) onSuccessCallback(itemId, index);

      return {
        id: itemId,
        totalItems: cart.total_items_count,
      };
    },
    [updateCartItemsCount, onSuccessCallback, setUserInfo, userId]
  );
};
